import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { recordPanelId, recordTabId } from "./recordTabIds";

export interface RecordTab {
  id: string;
  label: string;
  /** The tab's own URL, e.g. `/people/123/giving`. Tabs are routes, not local state. */
  href: string;
  /** Shown where meaningful (Giving 24, Notes 3); omit rather than show 0 for unknown. */
  count?: number;
  /**
   * Router state for the tab's link, e.g. the list the record was opened from, so a tab change
   * keeps the breadcrumb's way back to that list (filters restored).
   */
  state?: unknown;
}

export interface RecordRouteTabsProps {
  tabs: RecordTab[];
  activeTab: string;
  /** Accessible name for the tab list, e.g. "Sarah Garcia sections". */
  label: string;
  /** Id prefix: tab `i` is `${idBase}-tab-${id}`, and the panel is `${idBase}-panel`. */
  idBase: string;
  /**
   * The viewer can't open this record (NAVIGATION_AND_STATES.md, T2 no permission: "tabs
   * locked"). The tabs still show what the record has and stay reachable by keyboard, but they
   * are disabled, not links, so nothing navigates.
   */
  locked?: boolean;
  className?: string;
}


/**
 * Pill tabs routed by path segment (PAGE_COMPONENTS.md `RouteTabs`). ACCESSIBILITY.md § T2:
 * the `tablist` pattern — arrow keys, Home and End move between tabs, Tab moves into the
 * panel — and each tab is also a real link, so a tab change is a URL change and deep links,
 * new tabs and Back all work. Activation is manual: arrows move focus, Enter or Space opens
 * the tab.
 * Scrolls sideways on narrow screens.
 */
export function RecordRouteTabs({ tabs, activeTab, label, idBase, locked = false, className }: RecordRouteTabsProps) {
  const refs = useRef<Array<HTMLElement | null>>([]);
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));
  // Roving tabindex: the one Tab stop follows arrow-key focus, so Tab from a focused tab goes
  // into the panel. It snaps back to the selected tab whenever the selection (the route) moves.
  // Tracked by tab id, not position: a conditional tab (Volunteer) can appear or disappear
  // ahead of the focused one, and an index would then point at a different tab.
  const [focusedId, setFocusedId] = useState(activeTab);
  useEffect(() => setFocusedId(activeTab), [activeTab]);
  const focusedIndex = tabs.findIndex((tab) => tab.id === focusedId);
  // If the remembered tab is gone, the selected tab is the Tab stop.
  const focusIndex = focusedIndex === -1 ? activeIndex : focusedIndex;
  const setFocusIndex = (index: number) => setFocusedId(tabs[index]?.id ?? activeTab);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    // Modified keys belong to the browser and assistive tech (Alt+Left is Back, Cmd+Left is
    // Back on macOS), so only bare keys drive the tab list.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    // Space activates a tab like Enter (a link only does Enter natively; Space would scroll).
    if (event.key === " ") {
      event.preventDefault();
      if (!locked) refs.current[index]?.click();
      return;
    }
    const last = tabs.length - 1;
    const next = event.key === "ArrowRight" ? (index === last ? 0 : index + 1)
      : event.key === "ArrowLeft" ? (index === 0 ? last : index - 1)
      : event.key === "Home" ? 0
      : event.key === "End" ? last
      : null;
    if (next === null) return;
    event.preventDefault();
    setFocusIndex(next);
    refs.current[next]?.focus();
  };

  return (
    <div className={cn("-mx-1 overflow-x-auto px-1 pb-1", className)}>
      <div role="tablist" aria-label={label} className="flex w-max min-w-full items-center gap-1 border-b border-hairline">
        {tabs.map((tab, index) => {
          const current = tab.id === activeTab;
          const tabProps = {
            role: "tab",
            id: recordTabId(idBase, tab.id),
            "aria-selected": current,
            // Every tab names the one panel it opens into, so the relationship is announced
            // while arrowing, before activation.
            "aria-controls": recordPanelId(idBase),
            tabIndex: index === focusIndex ? 0 : -1,
            onKeyDown: (event: KeyboardEvent<HTMLElement>) => onKeyDown(event, index),
            onFocus: () => setFocusIndex(index),
            className: cn(
              "relative inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-t-lg px-[18px] text-[13px] text-muted-foreground transition-colors after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:bg-transparent focus-visible:outline-none focus-visible:ring-[var(--focus-ring-width)] focus-visible:ring-ring focus-visible:ring-offset-2 lg:min-h-9",
              locked ? "cursor-not-allowed opacity-60" : "hover:text-foreground",
              // DECISIONS.md D5: an underline, 2px in the module accent (#7915), under foreground text (AA in every theme).
              current && "font-semibold text-foreground after:bg-module",
            ),
          } as const;
          const content = (
            <>
              {tab.label}
              {tab.count !== undefined
                ? <span className="rounded-full bg-muted px-1.5 text-[11px] font-medium tabular-nums text-muted-foreground">{tab.count}</span>
                : null}
            </>
          );
          return locked
            ? (
              <span key={tab.id} ref={(el) => { refs.current[index] = el; }} aria-disabled="true" {...tabProps}>
                {content}
                <Lock className="size-3" aria-hidden="true" />
              </span>
            )
            : (
              <Link key={tab.id} ref={(el) => { refs.current[index] = el; }} to={tab.href} state={tab.state} {...tabProps}>
                {content}
              </Link>
            );
        })}
      </div>
    </div>
  );
}
